import * as yup from 'yup'
import { Home } from 'react-feather'
import { toast } from 'react-toastify'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { CardTitle, Input } from 'reactstrap'
import { Link, useHistory } from 'react-router-dom'
import { Formik, Form, useFormikContext } from 'formik'
import Breadcrumb from '~/components/Breadcrumb'
import { Col, LoadableCard } from '~/components/commons'
import DropzoneImageInput from '~/components/DropzoneImageInput'
import Textarea from '~/components/Textarea'
import WebsiteTrackedInput from '~/components/WebsiteTrackedInput'
import { requiredMessage } from './commons/validations'
import {
  ScheduleSubmitForm,
  Field,
  FormContainer,
  Container,
  Rule,
  FilterPanel,
  validations,
  PostDateRangeField,
} from './commons'
import { dateToGoogle, timeToGoogleAPI } from '~/utility/date'
import PostPreviewModal from './previews/PostPreviewModal'
import { Alert } from '~/components/Alert'
import uploadFile from '~/api/uploadFile'
import { updateSchedule } from '~/api/updateSchedule'

const validationSchema = () =>
  yup.lazy(({ media }) =>
    yup.object().shape({
      media: !media?.length
        ? yup.array().nullable(true)
        : yup.array().of(validations.media),
      summary: validations.summary.required(requiredMessage),
      event: validations.event,
      offer: validations.offer,
      locationNames: validations.locationNames,
      schedulePost: validations.schedulePost,
    })
  )

function NewOfferForm({ setShowHours, initialValue }) {
  const formik = useFormikContext()

  return (
    <FormContainer>
      <Field
        name="media"
        label="Add photo"
        render={DropzoneImageInput}
        initialValue={initialValue?.media}
        errorExtractor={error => error[0]}
        controller={formik}
        type="file"
        helperText="The minimum size is 400px by 300px (recommended 1200px by 900px or 4:3 ratio). JPG or PNG."
        onChange={(files, helpers) => helpers.setValue(files)}
      />

      <Field
        name="event.title"
        label="Offer title"
        render={Input}
        required
        enableVariables
      />

      <PostDateRangeField
        setShowHours={setShowHours}
        initialValue={initialValue}
      />

      <Field
        name="summary"
        label="Offer details"
        render={Textarea}
        maxLength={1500}
        resize="true"
        enableVariables
        required
      />

      <Field name="offer.couponCode" label="Cupom code" render={Input} />

      <Field
        label="Link to redeem offer"
        name="offer.redeemOnlineUrl"
        render={WebsiteTrackedInput}
        initialValue={initialValue?.offer?.redeemOnlineUrl}
        onChange={(value, helpers) => helpers.setValue(value)}
      />

      <Field
        label="Terms and Conditions"
        name="offer.termsConditions"
        render={Textarea}
      />
    </FormContainer>
  )
}

function EditOfferPost({ initialValue, scheduleId }) {
  const intl = useIntl()

  const [alertVisible, setAlertVisible] = useState({
    error: false,
    success: false,
  })
  const [modalMessage, setModalMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const history = useHistory()
  const [opened, setOpened] = useState(false)
  const [dataModal, setDataModal] = useState({})
  const [submitData, setSubmitData] = useState(null)
  const [showHours, setShowHours] = useState(false)
  const [valid, setValid] = useState(false)

  function openModal(schedulePost) {
    setModalMessage(
      `Post successfully ${schedulePost ? 'scheduled' : 'created'}!`
    )

    setAlert('success', true)
  }

  const toggle = () => {
    if (submitting) return
    setOpened(prev => !prev)
  }

  function openPreviewModal(values) {
    handleSubmit(values, true)
      .then(() => {
        setValid(true)
        toast.success(
          intl.formatMessage({ id: 'The information to be updated is valid!' })
        )
      })
      .catch(error => {
        toast.success(
          intl.formatMessage({
            id: 'The information to be updated is invalid!',
          })
        )

        setValid(false)
        setModalMessage(error?.message)
        setAlert('error', true)
      })
      .finally(() => {
        const { event, media, schedulePost, summary, offer } = values
        setSubmitData(values)
        setDataModal({
          title: event.title,
          schedule: schedulePost,
          media,
          startDate: event?.schedule?.startDateTime,
          endDate: event?.schedule?.endDateTime,
          details: summary,
          couponCode: offer?.couponCode,
          offerLink: offer?.redeemOnlineUrl,
          terms: offer?.termsConditions,
        })

        setOpened(true)
      })
  }

  async function handleSubmit(values, validate = false) {
    setSubmitting(true)
    try {
      const { locationNames, event, ...postBody } = values

      const data = {
        locationNames,
        post: {
          ...postBody,
          topicType: 'OFFER',
          event: {
            ...event,
            schedule: {
              startDate: dateToGoogle(event.schedule.startDateTime),
              endDate: dateToGoogle(event.schedule.endDateTime),
            },
          },
        },
        validate,
      }

      if (showHours) {
        data.post.event.schedule.startTime = timeToGoogleAPI(
          event.schedule.startDateTime
        )
        data.post.event.schedule.endTime = timeToGoogleAPI(
          event.schedule.endDateTime
        )
      }

      if (values.media.length) {
        const { data: picture } = await uploadFile(values.media)
        data.post.media = {
          mediaFormat: 'PHOTO',
          sourceUrl: picture.uri,
        }
      }

      const res = await updateSchedule(scheduleId, data)
      if (!res.data) {
        throw new Error()
      }

      if (!validate) openModal(values.schedulePost)

      setSubmitting(false)
    } catch (error) {
      setSubmitting(false)
      if (!validate) {
        toast.error(
          `Not possible to ${
            values.schedulePost ? 'schedule' : 'create'
          } your post.`
        )
      } else {
        const errorMessage = error?.response?.data || 'Invalid title size!'
        throw new Error(errorMessage)
      }
    }
  }

  function setAlert(name, value) {
    setAlertVisible(old => ({
      ...old,
      [name]: value,
    }))
  }

  return (
    <Col>
      <Breadcrumb
        className="mb-2"
        paths={[
          {
            render: () => (
              <Link className="d-flex align-items-center" to="/">
                <Home size={20} />
              </Link>
            ),
          },
          { text: 'Posts' },
          { text: 'Offer' },
        ]}
      />

      <LoadableCard loading={submitting} style={{ padding: '30px 40px' }}>
        <CardTitle>
          <FormattedMessage id="Edit Offer Post" />
        </CardTitle>
        <Formik
          initialValues={initialValue}
          onSubmit={openPreviewModal}
          validationSchema={validationSchema}
          validateOnBlur={false}
          validateOnChange={false}
          validateOnMount={false}
        >
          {() => (
            <Form>
              <Container>
                <div className="mt-1">
                  <NewOfferForm
                    setShowHours={setShowHours}
                    initialValue={initialValue}
                  />
                </div>
                <Rule />
                <div className="mt-1">
                  <FilterPanel
                    initialValue={{ location: initialValue?.locationNames }}
                  />
                  <ScheduleSubmitForm
                    initialDate={initialValue?.schedulePost}
                  />
                </div>
                <PostPreviewModal
                  toggleModal={toggle}
                  isOpen={opened}
                  modalData={dataModal}
                  onSubmit={() => handleSubmit(submitData)}
                  isSubmiting={submitting}
                  typePost="offer"
                  isValid={valid}
                  showHours={showHours}
                />
              </Container>
            </Form>
          )}
        </Formik>
      </LoadableCard>
      <Alert
        type="error"
        visible={alertVisible.error}
        onClose={() => setAlert('error', false)}
        onCancel={() => setAlert('error', false)}
        confirmBtnText="Try again"
        body={modalMessage}
        title="Error"
        closeOnClickOutside
      />
      <Alert
        type="success"
        visible={alertVisible.success}
        onClose={() => history.push('/')}
        confirmBtnText="Go to dashboard"
        hasCancelBtn
        cancelBtnText="Make new post"
        onCancel={() => history.push('/posts/offer')}
        body={modalMessage}
        title="Success"
      />
    </Col>
  )
}

export default EditOfferPost
